import React, { useEffect, useState, Dispatch, SetStateAction } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, HStack, VStack, Text } from "native-base";
import * as AV from "../../AVCore/AVCore";
import { setEntitydata } from "../../Redux/Actions/userActions";
import { RootStateOrAny, useSelector, useDispatch } from "react-redux";
import AVCancel from "../../components/common/AVCancel";
import AVDropdown from "../../components/AVDropdown";
import inform from "../../assets/images/inform.png";
import { Loc } from "../../App/Loc";
import { CreateLeagues, TickCircle } from "../../assets/icons/icon-assets";

type Props = {
  isSuccess?: boolean | null;
  inviteToken?: string | "";
  leagueTeamInviteFrom?: AV.ILink | null;
  showLeagueTeamInvitePopup?: boolean;
  setShowLeagueTeamInvitePopup: Dispatch<SetStateAction<boolean>>;
};

type PopupType = "select" | "success";

type DropdownOption = AV.ILink & {
  value: string;
  labelTeam: string;
  labelSeason: string;
};

const AVLeagueInvitePopup = ({
  isSuccess,
  inviteToken,
  leagueTeamInviteFrom,
  showLeagueTeamInvitePopup,
  setShowLeagueTeamInvitePopup,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const currentUser = useSelector(
    (state: RootStateOrAny) => state.user.user?.[0],
  );
  const entityDataList = useSelector(
    (state: RootStateOrAny) => state.user.entitydatalist,
  );

  const [isLoader, setIsLoader] = useState(false);
  const [popupType, setPopupType] = useState<PopupType>(
    isSuccess ? "success" : "select",
  );
  const [selectedSeason, setSelectedSeason] = useState("");
  const [seasonOptions, setSeasonOptions] = useState<DropdownOption[]>([]);
  const [allTeamSeasonList, setAllTeamSeasonList] = useState<AV.ILink[]>([]);

  useEffect(() => {
    if (!currentUser?.orgs) return;

    const teamSeasonList = currentUser.orgs.filter(
      (x: AV.ILink) => x.type === "PlayerOrg",
    );
    setAllTeamSeasonList(teamSeasonList);

    const options: DropdownOption[] = teamSeasonList.map((x: AV.ILink) => ({
      ...x,
      value: x.id ?? "",
      labelTeam: x.title ?? "",
      labelSeason: x.data ?? x.title ?? "",
    }));

    setSeasonOptions(options);

    if (isSuccess) {
      setPopupType("success");
      return;
    } else setPopupType("select");
  }, [currentUser]);

  const handleClose = () => {
    setShowLeagueTeamInvitePopup?.(false);

    if (isSuccess) {
      const leagueteam = location?.state?.team;
      navigate(`/teams/${leagueteam?.id}/${leagueteam?.region}`, {
        state: { isCreate: false, team: leagueteam, newEntity: true },
      });
    }
  };

  const handleCreateSeason = () => {
    setShowLeagueTeamInvitePopup?.(false);
    navigate(
      `/organizations/${leagueTeamInviteFrom?.id}/${leagueTeamInviteFrom?.region}/${leagueTeamInviteFrom?.type}`,
      {
        state: {
          type: "Team",
          isCreate: true,
          inviteToken: inviteToken,
          team: leagueTeamInviteFrom,
          orgType: leagueTeamInviteFrom?.type,
          softLinkId: leagueTeamInviteFrom?.data,
        },
      },
    );
  };

  const handleAccept = async () => {
    if (
      !leagueTeamInviteFrom ||
      !leagueTeamInviteFrom.data ||
      !leagueTeamInviteFrom.region
    ) {
      return;
    }

    setIsLoader(true);

    try {
      const softLinkObj: AV.ILink = {
        id: leagueTeamInviteFrom.data,
        pk: leagueTeamInviteFrom.data.slice(-4),
        region: leagueTeamInviteFrom.region,
        title: "",
        data: "",
        type: "PlayerOrg",
        subType: "Softlink",
        thumb: null,
        background: null,
      };

      // Get League Season & Accepted Team Season
      const leagueSeason = await getEntity(leagueTeamInviteFrom);
      const teamSeason = await getEntity(
        allTeamSeasonList?.find((x) => x?.id === selectedSeason),
      );
      if (!leagueSeason || !teamSeason) {
        return;
      }

      // Replace SoftLink with Team Season inside League Season
      await AV.AVEntity.checkLinks(leagueSeason, teamSeason, softLinkObj);

      // Find all LeagueGameBase entities that contain this SoftLink
      const leagueGames =
        leagueSeason.childOrgs?.filter((gm) => {
          if (gm.type !== "LeagueGameBase") return false;
          const teams: AV.ILink[] = JSON.parse(gm.data ?? "[]");
          return teams.some(
            (team) => team.id === softLinkObj.id && team.subType === "Softlink",
          );
        }) ?? [];

      // Update each LeagueGameBase and create Team Game
      for (const gameLink of leagueGames) {
        const leagueGame = await getEntity(gameLink, gameLink.subType);
        if (!leagueGame) continue;

        // Replace SoftLink with Team Season
        const updatedLeagueGame = (await AV.AVEntity.checkLinks(
          leagueGame,
          teamSeason,
          softLinkObj,
        )) as AV.IGame;

        // Create Team Game Event
        const teamGamePayload: AV.IGame = {
          type: "Game",
          eventType: "Game",
          name: updatedLeagueGame.name,
          start: updatedLeagueGame.start,
          end: updatedLeagueGame.end,
          playerStart: updatedLeagueGame.playerStart,
          //teams: updatedLeagueGame?.teams,
          parent: AV.AVEntity.getLink(teamSeason),
          topParent: updatedLeagueGame.parent,
          scoreType: updatedLeagueGame.scoreType,
          location: updatedLeagueGame.location,
          locURL: updatedLeagueGame.locURL,
          tz: updatedLeagueGame.tz,
          links: updatedLeagueGame.links,
          league: AV.AVEntity.getLink(leagueSeason),
          leagueGame: AV.AVEntity.getFk(updatedLeagueGame),
          preferences: {
            isPrivate: false,
          } as AV.IPreferences,
        };

        await AV.AVEntity.save(teamGamePayload);
      }

      // Notify League Owner
      await AV.Person.acceptInviteTeam({
        source: AV.AVEntity.getFk(leagueSeason),
        target: AV.AVEntity.getLink(teamSeason),
        softLink: softLinkObj,
      });

      // Delete Invite Token
      await AV.AVEntity.processToken({
        token: inviteToken,
        entity: leagueTeamInviteFrom,
        type: "LeagueTeamInvite",
        data: "Delete",
      });

      setPopupType("success");
    } catch (error) {
      console.error("Failed to accept team invitation:", error);
    } finally {
      setIsLoader(false);
    }
  };

  const handleGoToLeague = () => {
    setShowLeagueTeamInvitePopup?.(false);
    navigate(
      `/organizations/${leagueTeamInviteFrom?.id}/${leagueTeamInviteFrom?.region}/${leagueTeamInviteFrom?.type}`,
      {
        state: {
          isCreate: false,
          orgType: leagueTeamInviteFrom?.type,
          team: leagueTeamInviteFrom,
        },
      },
    );
  };

  const getEntity = async (entityFK, activity = "") => {
    if (!entityFK?.id) return null;

    let entity = entityDataList?.find((x) => x?.id === entityFK?.id) ?? null;

    if (!entity) {
      let entityAPI: AV.IAVEntity | null = null;

      if (entityFK.type === "LeagueGameBase") {
        const param: AV.ILeagueGameParam = {
          source: entityFK,
          scoreType: activity,
        };
        entityAPI = await AV.Game.getLeagueGame(param);
      } else {
        entityAPI = await AV.AVEntity.get(entityFK);
      }

      if (entityAPI) {
        const updatedEntityData = [...(entityDataList || []), entityAPI];
        dispatch(setEntitydata(updatedEntityData));
        entity = entityAPI;
      }
    }

    return entity;
  };

  if (!showLeagueTeamInvitePopup) return null;

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      backgroundColor="rgba(0,0,0,.35)"
      zIndex={9999}
    >
      <Box bg="white" rounded="xl" width="650px" p={8} shadow={5}>
        {/* Header */}
        <HStack justifyContent="space-between" alignItems="center" mb={6}>
          <Text fontSize="22px" fontWeight="700">
            {Loc.currentLang.leagueInvite.title}
          </Text>

          <AVCancel callback={handleClose} color={undefined} size={undefined} />
        </HStack>

        {/* ---------------- SELECT ---------------- */}
        {popupType === "select" && (
          <VStack space={6}>
            {/* Hero Section */}
            <HStack space={4} alignItems="center">
              <Box
                w="52px"
                h="52px"
                rounded="full"
                bg="rgba(50,174,120,0.12)"
                alignItems="center"
                justifyContent="center"
              >
                <CreateLeagues width={"30"} height={"30"} color={"#32AE78"} />
              </Box>

              <VStack flex={1}>
                <Text fontSize="24px" fontWeight="700" color="coolGray.800">
                  {Loc.currentLang.leagueInvite.joinLeague}
                </Text>

                <Text fontSize="15px" color="coolGray.500">
                  {Loc.currentLang.leagueInvite.joinLeagueDesc}{" "}
                  <Text fontWeight="600" color="coolGray.700">
                    {leagueTeamInviteFrom?.title}
                  </Text>
                  .
                </Text>
              </VStack>
            </HStack>

            {/* Dropdown */}
            <VStack space={2}>
              <Text fontWeight="600" fontSize="15px">
                {Loc.currentLang.leagueInvite.teamSeason}
              </Text>

              <AVDropdown
                label={Loc.currentLang.leagueInvite.selectTeamSeason}
                options={seasonOptions}
                value={selectedSeason}
                onChange={(e) => setSelectedSeason(e.target.value)}
              />
            </VStack>

            {/* Info */}
            <Box
              bg="rgba(50,174,120,.08)"
              borderRadius="10px"
              borderWidth={1}
              borderColor="rgba(50,174,120,.18)"
              px={4}
              py={3}
            >
              <HStack space={3} alignItems="flex-start">
                <img src={inform} width="20" height="20" />

                <VStack flex={1}>
                  <Text fontWeight="600" color="coolGray.700">
                    {Loc.currentLang.leagueInvite.noTeamSeasonTitle}
                  </Text>

                  <Text color="coolGray.500" mt={1} fontSize="14px">
                    {Loc.currentLang.leagueInvite.noTeamSeasonDesc}
                  </Text>
                </VStack>
              </HStack>
            </Box>

            {/* Footer */}
            <HStack justifyContent="space-between" alignItems="center">
              <Button
                variant="outline"
                borderColor="AVColor.primary"
                rounded="lg"
                h="44px"
                px={6}
                isDisabled={isLoader}
                onPress={handleCreateSeason}
              >
                <Text color="AVColor.primary" fontWeight="600">
                  {Loc.currentLang.leagueInvite.createNewTeam}
                </Text>
              </Button>

              <HStack space={3}>
                <Button
                  variant="ghost"
                  rounded="lg"
                  isDisabled={isLoader}
                  onPress={handleClose}
                >
                  {Loc.currentLang.leagueInvite.cancel}
                </Button>

                <Button
                  rounded="lg"
                  h="44px"
                  px={8}
                  bg="AVColor.primary"
                  _hover={{ bg: "AVColor.primary" }}
                  _pressed={{ bg: "AVColor.primary" }}
                  isLoading={isLoader}
                  isDisabled={!selectedSeason || isLoader}
                  onPress={handleAccept}
                >
                  {Loc.currentLang.leagueInvite.accept}
                </Button>
              </HStack>
            </HStack>
          </VStack>
        )}

        {/* ---------------- SUCCESS ---------------- */}
        {popupType === "success" && (
          <VStack space={6} alignItems="center">
            {/* Success Icon */}
            <TickCircle width="88px" height="88px" />

            {/* Title */}
            <VStack alignItems="center" space={1}>
              <Text fontSize="28px" fontWeight="700" color="AVColor.primary">
                {Loc.currentLang.leagueInvite.inviteAccepted}
              </Text>

              <Text color="coolGray.500" textAlign="center" fontSize="16px">
                {Loc.currentLang.leagueInvite.inviteAcceptedDesc}
              </Text>

              <Text fontSize="22px" fontWeight="700" textAlign="center">
                {leagueTeamInviteFrom?.title}
              </Text>
            </VStack>

            <Button
              bg="AVColor.primary"
              _hover={{ bg: "AVColor.primary" }}
              _pressed={{ bg: "AVColor.primary" }}
              rounded="lg"
              h="48px"
              w="240px"
              onPress={handleGoToLeague}
            >
              {Loc.currentLang.leagueInvite.goToLeague}
            </Button>
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default AVLeagueInvitePopup;
